import { useCallback, useMemo, useState } from 'react'
import { Button, GoodsFamily, PageSection, ReportingPeriodBasis } from '@/common'
import useI18n from '@/shared/hooks/usei18n'
import { ImportedGood } from '@/features/cbam-reporting/hooks/importedGoods'
import type { ImportedGoodExtended } from '@/features/cbam-reporting/hooks/useImportedGoodsFilters'
import { Partnership } from '@/features/suppliers/hooks/partnerships'
import { Installation } from '@/features/suppliers/hooks/installations'
import { Importer } from '@/features/importers/hooks/importers'
import { SupplierEmissionsReport } from '@/features/suppliers/hooks/supplierEmissionsReport'
import { BatchLiabilityResponse } from '@/features/cbam-reporting/hooks/cbamLiability'
import {
  getSupplierEmissionsButtonState,
  type InstallationForEligibility
} from '@/shared/utils/batchUpdateSelectionRules'
import { getImportedGoodEligibility } from '@/shared/utils/importedGoodEligibility'
import type { ImportedGoodsFiltersResult } from '../../../hooks/useImportedGoodsFilters'
import { useImportedGoodsSelection } from '../../../hooks/useImportedGoodsSelection'
import useDialog from '@/common/hooks/useDialog'
import { useGoodsCodes } from '@/shared/hooks/goodsCodes'
import MissingResourcesAlert from '@/shared/components/MissingResourcesAlert'
import ImportedGoodsFilterBar from './ImportedGoodsFilterBar'
import ImportedGoodsList from './list/ImportedGoodsList'
import ImportedGoodDetailsDrawer from './ImportedGoodDetailsDrawer'
import BulkUpdateSupplierEmissionsDialog from '../dialogs/BulkUpdateSupplierEmissionsDialog'
import BulkUpdateCustomsDialog from '../dialogs/BulkUpdateCustomsDialog'
import BulkUpdateImporterDialog from '../dialogs/BulkUpdateImporterDialog'
import BulkDeleteImportedGoodsDialog from '../dialogs/BulkDeleteImportedGoodsDialog'
import ImportedGoodEditDialog from '../dialogs/ImportedGoodEditDialog'
import ImportedGoodDeleteDialog from '../dialogs/ImportedGoodDeleteDialog'
import type { CbamModifiers } from '@/shared/types/cbamModifiers'

type ImportedGoodsSectionProps = {
  goods: ImportedGoodExtended[]
  partnerships: Partnership[]
  installations: Installation[]
  supplierEmissionsReports: SupplierEmissionsReport[]
  importers: Importer[]
  liabilityData: BatchLiabilityResponse | null
  filters: ImportedGoodsFiltersResult
  isRepresentative: boolean
  isReportCompleted: boolean
  organizationId: string
  onOpenUpload: () => void
  onOpenAddGoods: () => void
  reportingPeriodYear: number
  periodId: string
  isExportCsvEnabled: boolean
  isExportCsvLoading: boolean
  onOpenExportCsv: () => void
  cbamReportId: string
  modifiers: CbamModifiers
}

export default function ImportedGoodsSection({
  goods,
  partnerships,
  installations,
  supplierEmissionsReports,
  importers,
  liabilityData,
  filters,
  isRepresentative,
  isReportCompleted,
  organizationId,
  onOpenUpload,
  onOpenAddGoods,
  reportingPeriodYear,
  periodId,
  isExportCsvEnabled,
  isExportCsvLoading,
  onOpenExportCsv,
  cbamReportId,
  modifiers
}: ImportedGoodsSectionProps) {
  const { t } = useI18n()

  const [importedGoodToEdit, setImportedGoodToEdit] = useState<ImportedGood | null>(null)
  const [importedGoodToView, setImportedGoodToView] = useState<ImportedGoodExtended | null>(
    null
  )
  const [importedGoodToDelete, setImportedGoodToDelete] = useState<ImportedGood | null>(null)
  const [resolvedSupplierName, setResolvedSupplierName] = useState<string>('')
  const [resolvedInstallationName, setResolvedInstallationName] = useState<string>('')
  const [resolvedGoodsFamily, setResolvedGoodsFamily] = useState<GoodsFamily | null>(null)

  const {
    open: isImportedGoodEditDialogOpen,
    openDialog: openImportedGoodEditDialog,
    closeDialog: closeImportedGoodEditDialog
  } = useDialog()
  const {
    open: isDeleteImportedGoodDialogOpen,
    openDialog: openDeleteImportedGoodDialog,
    closeDialog: closeDeleteImportedGoodDialog
  } = useDialog()

  const handleEditImportedGood = useCallback(
    (
      importedGood: ImportedGood,
      supplierName: string,
      installationName: string,
      goodsFamily?: GoodsFamily
    ) => {
      setImportedGoodToEdit(importedGood)
      setResolvedSupplierName(supplierName)
      setResolvedInstallationName(installationName)
      setResolvedGoodsFamily(goodsFamily ?? null)
      openImportedGoodEditDialog()
    },
    [openImportedGoodEditDialog]
  )
  const handleCloseImportedGoodEditDialog = useCallback(() => {
    setImportedGoodToEdit(null)
    setResolvedSupplierName('')
    setResolvedInstallationName('')
    setResolvedGoodsFamily(null)
    closeImportedGoodEditDialog()
  }, [closeImportedGoodEditDialog])
  const handleDeleteImportedGood = useCallback(
    (importedGood: ImportedGood) => {
      setImportedGoodToDelete(importedGood)
      openDeleteImportedGoodDialog()
    },
    [openDeleteImportedGoodDialog]
  )
  const handleViewImportedGood = useCallback((importedGood: ImportedGoodExtended) => {
    setImportedGoodToView(importedGood)
  }, [])
  const handleCloseViewDialog = useCallback(() => {
    setImportedGoodToView(null)
  }, [])

  const {
    activeFilters,
    filterOptions,
    filteredGoods,
    addFilter,
    removeFilter,
    clearAllFilters,
    hasActiveFilters,
    toggleFilters,
    setShowOnlyDefaultValues,
    setShowOnlyIncomplete
  } = filters

  const {
    selectedItems,
    selectedCount,
    isSelected,
    toggleSelect,
    toggleSelectAll,
    deselectAll,
    isAllSelected,
    isPartiallySelected,
    hasSelection
  } = useImportedGoodsSelection(filteredGoods)

  const { data: goodsCodes } = useGoodsCodes({})
  const installationsForEligibility = useMemo((): InstallationForEligibility[] => {
    return installations.map(installation => ({
      id: installation.id,
      country: installation.country,
      goodsFamily: installation.goodsFamily,
      partnershipId: installation.partnershipId
    }))
  }, [installations])
  const getAggregatedCategoryForCnCode = useMemo(() => {
    const byCn = new Map(
      (goodsCodes ?? []).map(gc => [gc.goodsCode, gc.aggregatedGoodsCategory])
    )
    return (cnCode: string) => byCn.get(cnCode)
  }, [goodsCodes])
  const supplierEmissionsButtonState = useMemo(
    () =>
      getSupplierEmissionsButtonState({
        selectedGoods: selectedItems.map(item => ({
          country: item.country,
          cnCode: item.cnCode
        })),
        installations: installationsForEligibility,
        getAggregatedCategoryForCnCode
      }),
    [selectedItems, installationsForEligibility, getAggregatedCategoryForCnCode]
  )

  const liabilityDataByGoodId = useMemo(() => {
    const resultById = new Map<string, NonNullable<BatchLiabilityResponse['results']>[number]>()
    for (const result of liabilityData?.results ?? []) {
      if (result.id) {
        resultById.set(result.id, result)
      }
    }
    return resultById
  }, [liabilityData?.results])
  const viewDrawerData = useMemo(() => {
    if (!importedGoodToView) {
      return null
    }
    const viewLiabilityData = liabilityDataByGoodId.get(importedGoodToView.id) ?? null
    const viewSupplier =
      partnerships.find(partnership => partnership.id === importedGoodToView.partnershipId) ?? null
    const viewImporter =
      importers.find(importer => importer.id === importedGoodToView.importerId) ?? null
    const viewInstallation =
      installations.find(installation => installation.id === importedGoodToView.installationId) ??
      null
    const viewSupplierEmissionsReport = importedGoodToView.supplierEmissionsReportId
      ? (supplierEmissionsReports.find(
          report => report.id === importedGoodToView.supplierEmissionsReportId
        ) ?? null)
      : null
    const viewEligibility = getImportedGoodEligibility(importedGoodToView, isRepresentative)
    const viewAggregatedGoodsCategory = getAggregatedCategoryForCnCode(importedGoodToView.cnCode)
    return {
      liabilityData: viewLiabilityData,
      supplier: viewSupplier ?? null,
      importer: viewImporter ?? null,
      installation: viewInstallation ?? null,
      supplierEmissionsReport: viewSupplierEmissionsReport ?? null,
      eligibility: viewEligibility,
      aggregatedGoodsCategory: viewAggregatedGoodsCategory
    }
  }, [
    importedGoodToView,
    liabilityDataByGoodId,
    partnerships,
    importers,
    installations,
    supplierEmissionsReports,
    isRepresentative,
    getAggregatedCategoryForCnCode
  ])

  const {
    open: isBulkUpdateSupplierEmissionsDialogOpen,
    openDialog: openBulkUpdateSupplierEmissionsDialog,
    closeDialog: closeBulkUpdateSupplierEmissionsDialog
  } = useDialog()
  const {
    open: isBulkUpdateCustomsDialogOpen,
    openDialog: openBulkUpdateCustomsDialog,
    closeDialog: closeBulkUpdateCustomsDialog
  } = useDialog()
  const {
    open: isBulkUpdateImporterDialogOpen,
    openDialog: openBulkUpdateImporterDialog,
    closeDialog: closeBulkUpdateImporterDialog
  } = useDialog()
  const {
    open: isBulkDeleteDialogOpen,
    openDialog: openBulkDeleteDialog,
    closeDialog: closeBulkDeleteDialog
  } = useDialog()

  const hasReportedGoods = goods.length > 0

  return (
    <>
      <PageSection
        title={t('cbamReporting:cbamReportDetails.reportedGoods.title')}
        actions={
          hasReportedGoods && (
            <div className="flex gap-2 items-center flex-wrap">
              <Button
                variant="subtle"
                disabled={!isExportCsvEnabled}
                isLoading={isExportCsvLoading}
                tooltip={
                  !isExportCsvEnabled
                    ? t('cbamReporting:cbamReportDetails.actions.exportCsvDisabledTooltip')
                    : undefined
                }
                onClick={onOpenExportCsv}
              >
                {t('cbamReporting:cbamReportDetails.actions.exportCsv')}
              </Button>
              <Button disabled={isReportCompleted} variant="subtle" onClick={onOpenUpload}>
                {t('cbamReporting:importedGoodsUpload.button')}
              </Button>
              <Button disabled={isReportCompleted} onClick={onOpenAddGoods}>
                {t('cbamReporting:cbamReportingPeriodCard.actions.addGoods')}
              </Button>
            </div>
          )
        }
      >
        {!hasReportedGoods ? (
          <div className="flex flex-col gap-4">
            <MissingResourcesAlert
              title={t('cbamReporting:cbamReportDetails.reportedGoods.missingGoods.title')}
              description={t(
                'cbamReporting:cbamReportDetails.reportedGoods.missingGoods.description'
              )}
              actionChildren={
                <div className="flex items-center gap-2">
                  <Button variant="default" onClick={onOpenAddGoods}>
                    {t('cbamReporting:cbamReportDetails.reportedGoods.missingGoods.action')}
                  </Button>
                  or
                  <Button variant="subtle" onClick={onOpenUpload} disabled={isReportCompleted}>
                    {t('cbamReporting:importedGoodsUpload.button')}
                  </Button>
                </div>
              }
            />
          </div>
        ) : (
          <>
            <ImportedGoodsFilterBar
              filterOptions={filterOptions}
              activeFilters={activeFilters}
              onAddFilter={addFilter}
              onRemoveFilter={removeFilter}
              onClearAll={clearAllFilters}
              toggleFilters={toggleFilters}
              onToggleDefaultValues={setShowOnlyDefaultValues}
              onToggleIncomplete={setShowOnlyIncomplete}
              isRepresentative={isRepresentative}
            />
            {hasActiveFilters && filteredGoods.length === 0 ? (
              <div className="text-center py-8 text-text-secondary">
                {t('cbamReporting:filters.noResults')}
              </div>
            ) : (
              <>
                {hasActiveFilters && (
                  <div className="text-sm text-text-secondary mb-2">
                    {t('cbamReporting:filters.resultsCount', { count: filteredGoods.length })}
                  </div>
                )}
                <ImportedGoodsList
                  filteredGoods={filteredGoods}
                  partnerships={partnerships}
                  importers={importers}
                  installations={installations}
                  liabilityData={liabilityData}
                  isRepresentative={isRepresentative}
                  isCompleted={isReportCompleted}
                  isAllSelected={isAllSelected}
                  isPartiallySelected={isPartiallySelected}
                  hasSelection={hasSelection}
                  selectedCount={selectedCount}
                  isSelected={isSelected}
                  toggleSelect={toggleSelect}
                  toggleSelectAll={toggleSelectAll}
                  deselectAll={deselectAll}
                  onEdit={handleEditImportedGood}
                  onView={handleViewImportedGood}
                  onDelete={handleDeleteImportedGood}
                  onBulkUpdateSupplierEmissions={openBulkUpdateSupplierEmissionsDialog}
                  onBulkUpdateCustoms={openBulkUpdateCustomsDialog}
                  onBulkUpdateImporter={openBulkUpdateImporterDialog}
                  onBulkDelete={openBulkDeleteDialog}
                  isSupplierEmissionsBatchDisabled={supplierEmissionsButtonState.disabled}
                  supplierEmissionsBatchTooltip={supplierEmissionsButtonState.tooltipKey}
                />
              </>
            )}
          </>
        )}
      </PageSection>
      {hasSelection && (
        <>
          <BulkUpdateSupplierEmissionsDialog
            isOpen={isBulkUpdateSupplierEmissionsDialogOpen}
            close={closeBulkUpdateSupplierEmissionsDialog}
            selectedGoods={selectedItems}
            organizationId={organizationId}
            cbamReportId={cbamReportId}
            partnerships={partnerships}
            installations={installations}
            emissionsReports={supplierEmissionsReports}
            onSuccess={deselectAll}
            cbamReportYear={reportingPeriodYear}
          />
          <BulkUpdateCustomsDialog
            isOpen={isBulkUpdateCustomsDialogOpen}
            close={closeBulkUpdateCustomsDialog}
            selectedGoods={selectedItems}
            organizationId={organizationId}
            cbamReportId={cbamReportId}
            onSuccess={deselectAll}
          />
          <BulkUpdateImporterDialog
            isOpen={isBulkUpdateImporterDialogOpen}
            close={closeBulkUpdateImporterDialog}
            selectedGoods={selectedItems}
            organizationId={organizationId}
            cbamReportId={cbamReportId}
            importers={importers}
            onSuccess={deselectAll}
          />
          <BulkDeleteImportedGoodsDialog
            isOpen={isBulkDeleteDialogOpen}
            close={closeBulkDeleteDialog}
            selectedGoods={selectedItems}
            organizationId={organizationId}
            cbamReportId={cbamReportId}
            onSuccess={deselectAll}
          />
        </>
      )}
      {importedGoodToEdit && (
        <ImportedGoodEditDialog
          reportingPeriodYear={reportingPeriodYear}
          periodId={periodId}
          isOpen={isImportedGoodEditDialogOpen}
          close={handleCloseImportedGoodEditDialog}
          importedGood={importedGoodToEdit}
          supplierName={resolvedSupplierName}
          isRepresentative={isRepresentative}
          goodsFamily={resolvedGoodsFamily ?? undefined}
          basisForReportingPeriod={ReportingPeriodBasis.CALENDAR_YEAR}
          installationName={resolvedInstallationName}
          organizationId={organizationId}
          partnerships={partnerships}
          installations={installations}
          emissionsReports={supplierEmissionsReports}
        />
      )}
      {importedGoodToDelete && (
        <ImportedGoodDeleteDialog
          isOpen={isDeleteImportedGoodDialogOpen}
          close={closeDeleteImportedGoodDialog}
          organizationId={organizationId}
          importedGood={importedGoodToDelete}
        />
      )}
      {importedGoodToView && viewDrawerData && (
        <ImportedGoodDetailsDrawer
          reportingPeriodYear={reportingPeriodYear}
          isOpen={!!importedGoodToView}
          close={handleCloseViewDialog}
          importedGood={importedGoodToView}
          goodsReceivedReportFileName={importedGoodToView.goodsReceivedReportFileName}
          organizationId={organizationId}
          cbamReportId={cbamReportId}
          installation={viewDrawerData.installation}
          supplier={viewDrawerData.supplier}
          importer={viewDrawerData.importer}
          liabilityData={viewDrawerData.liabilityData}
          supplierEmissionsReport={viewDrawerData.supplierEmissionsReport}
          eligibility={viewDrawerData.eligibility}
          isRepresentative={isRepresentative}
          modifiers={modifiers}
          aggregatedGoodsCategory={viewDrawerData.aggregatedGoodsCategory}
        />
      )}
    </>
  )
}
